import React, { useState } from 'react';
import { Button, Modal } from '@getro/rombo';
import { HelpCircle } from 'lucide-react';
import { Box, Flex, Image, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import ImportContactsIcon from '../../../../assets/icon-getro-network.svg';

export const HowItWorks = ({ pointOfContact, networkName, sx }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const howItWorks = [
    {
      title: 'Connect your LinkedIn',
      description: 'We’ll fetch your connections while keeping them only visible to you.',
    },
    {
      title: 'See your contacts at these companies and offer intros',
      description: `Offer intros you find valuable, we’ll notify ${pointOfContact.fullName}.`,
    },
    {
      title: `${pointOfContact.fullName} sends you a forwardable email`,
      description: 'If they’d like to proceed, they’ll send you an email with more details, ready for you to forward.',
    },
    {
      title: 'You make the intro',
      description:
        'Review the email, add your context, and forward it to connect both people. Be sure to include them both in the email!',
    },
  ];

  return (
    <Box
      sx={{
        '[data-testid="body"]': {
          overflowY: 'scroll',
          scrollbarWidth: 'thin',
          scrollbarColor: '#dee3ed transparent',
        },
      }}
    >
      <Flex alignItems="center" sx={{ gap: '8px', cursor: 'pointer', ...sx }} onClick={() => setIsModalOpen(true)}>
        <Box as={HelpCircle} size="16px" aria-hidden="true" sx={{ strokeWidth: 1.5 }} />
        <Text fontSize="14px" fontWeight="medium" lineHeight="16px" display={['none', 'none', 'inline-block']}>
          How does this work?
        </Text>
      </Flex>
      {isModalOpen && (
        <Modal
          actions={[
            <Flex sx={{ justifyContent: 'flex-end' }}>
              <Box>
                <Button size="medium" onClick={() => setIsModalOpen(false)}>
                  Got it
                </Button>
              </Box>
            </Flex>,
          ]}
          isOpen
          onCancel={() => setIsModalOpen(false)}
        >
          <Box>
            <Flex sx={{ gap: '16px' }}>
              <Image
                src={ImportContactsIcon}
                alt="Getro Network"
                display={['none', 'block']}
                width="96px"
                height="96px"
                flex="0 0 auto"
              />
              <Flex sx={{ flexDirection: 'column', flexGrow: 1, gap: '8px' }}>
                <Text fontSize="18px" fontWeight="semibold" color="text.dark">
                  {`Help ${networkName} connect with the companies on this list`}
                </Text>
                <Text fontSize="16px" color="text.main">
                  {`${pointOfContact.fullName} is looking for warm intros to companies on this list. Connect your LinkedIn to find who you know and offer help.`}
                </Text>
              </Flex>
            </Flex>
            <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
              <Text mt="40px" fontWeight="600" color="text.dark">
                How it works
              </Text>
              <Flex sx={{ flexDirection: 'column', gap: '16px' }} as="ul" p="0">
                {howItWorks.map(({ title, description }, index) => (
                  <Flex as="li" sx={{ listStyle: 'none', gap: '8px' }}>
                    <Flex
                      alignItems="center"
                      justifyContent="center"
                      bg="purple.100"
                      width="24px"
                      height="24px"
                      minWidth="24px"
                      minHeight="24px"
                      sx={{ borderRadius: 'circle' }}
                    >
                      <Text fontSize="14px" fontWeight="medium" color="purple.400">
                        {index + 1}
                      </Text>
                    </Flex>
                    <Box>
                      <Text fontSize="14px" fontWeight="500" color="text.dark">
                        {title}
                      </Text>
                      <Text as="p" fontSize="14px">
                        {description}
                      </Text>
                    </Box>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          </Box>
        </Modal>
      )}
    </Box>
  );
};

HowItWorks.propTypes = {
  pointOfContact: PropTypes.object.isRequired,
  networkName: PropTypes.string.isRequired,
  sx: PropTypes.object,
};

HowItWorks.defaultProps = {
  sx: {},
};
